import React, { useState, useEffect } from 'react';
import { Plus, Trash2, Edit, X, MapPin, AlertCircle } from 'lucide-react';
import { api } from '../../services/api';
import './AdminUI.css';
import AdminTopbarPortal from './AdminTopbarPortal';

export default function AdminDeliveryZones() {
  const [zones, setZones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({
    name: '',
    mode: 'radius',
    maxDistanceMiles: '',
    postcodePattern: '',
    fee: '',
    minOrder: '',
    active: true
  });
  const [error, setError] = useState('');

  const load = async () => {
    try {
      const data = await api.getDeliveryZones();
      setZones(data);
    } catch (err) {
      console.error('Failed to load zones:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const openAddForm = () => {
    setForm({
      name: '',
      mode: 'radius',
      maxDistanceMiles: '',
      postcodePattern: '',
      fee: '',
      minOrder: '',
      active: true
    });
    setEditing(null);
    setShowForm(true);
    setError('');
  };

  const openEditForm = (zone) => {
    setForm({
      name: zone.name,
      mode: zone.mode || 'radius',
      maxDistanceMiles: zone.maxDistanceMiles?.toString() || '',
      postcodePattern: zone.postcodePattern || '',
      fee: zone.fee.toString(),
      minOrder: zone.minOrder?.toString() || '0',
      active: zone.active
    });
    setEditing(zone.id);
    setShowForm(true);
    setError('');
  };

  const handleSave = async () => {
    if (!form.name || !form.fee) {
      setError('Name and fee are required');
      return;
    }

    if (form.mode === 'radius' && !form.maxDistanceMiles) {
      setError('Max distance is required for radius zones');
      return;
    }

    if (form.mode === 'postcode' && !form.postcodePattern) {
      setError('Postcode pattern is required');
      return;
    }
    setError('');
    const payload = {
      name: form.name,
      mode: form.mode,
      maxDistanceMiles: form.mode === 'radius' ? parseFloat(form.maxDistanceMiles) : null,
      postcodePattern: form.mode === 'postcode' ? form.postcodePattern.toUpperCase() : null,
      fee: parseFloat(form.fee),
      minOrder: parseFloat(form.minOrder || 0),
      active: form.active
    };
    try {
      if (editing) {
        await api.updateDeliveryZone(editing, payload);
      } else {
        await api.createDeliveryZone(payload);
      }
      await load();
      setShowForm(false);
      setEditing(null);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to save zone');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this delivery zone?')) return;
    try {
      await api.deleteDeliveryZone(id);
      await load();
    } catch (err) {
      alert('Failed to delete zone');
    }
  };

  if (loading) return <div className="flex justify-center py-16"><div className="w-7 h-7 border-2 border-amber-600 border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div className="admin-view space-y-4">
      <AdminTopbarPortal><div className="admin-topbar-controls">
        <div>
          <h2 className="text-lg font-bold text-gray-900">Delivery Zones</h2>
          <p className="text-gray-500 text-sm">Configure distance-based delivery fees</p>
        </div>
        <button onClick={openAddForm}
          className="flex items-center gap-2 bg-gray-900 text-white px-4 py-2.5 rounded-xl text-sm font-semibold hover:bg-amber-700 transition-colors">
          <Plus className="w-4 h-4" /> Add Zone
        </button>
      </div></AdminTopbarPortal>

      {showForm && (
        <div className="bg-white rounded-2xl border border-gray-200 p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-gray-900">{editing ? 'Edit Delivery Zone' : 'New Delivery Zone'}</h3>
            <button onClick={() => { setShowForm(false); setEditing(null); setError(''); }}
              className="p-1.5 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100">
              <X className="w-5 h-5" />
            </button>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">

                        <div className="sm:col-span-2">
              <label className="text-xs text-gray-500 mb-1 block">Zone Type</label>
              <div className="grid grid-cols-2 gap-2 bg-gray-100 p-1 rounded-xl">
                <button
                  type="button"
                  onClick={() => setForm(p => ({ ...p, mode: 'radius' }))}
                  className={`py-2 rounded-lg text-sm font-semibold ${
                    form.mode === 'radius' ? 'bg-white shadow text-gray-900' : 'text-gray-500'
                  }`}
                >
                  Radius
                </button>
                <button
                  type="button"
                  onClick={() => setForm(p => ({ ...p, mode: 'postcode' }))}
                  className={`py-2 rounded-lg text-sm font-semibold ${
                    form.mode === 'postcode' ? 'bg-white shadow text-gray-900' : 'text-gray-500'
                  }`}
                >
                  Postcode
                </button>
              </div>
            </div>

            <div className="sm:col-span-2">
              <label className="text-xs text-gray-500 mb-1 block">Zone Name *</label>
              <input type="text" placeholder="e.g. Local, Nearby, Extended" value={form.name}
                onChange={e => setForm(p => ({ ...p, name: e.target.value }))}
                className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-amber-500 focus:ring-1 focus:ring-amber-500" />
            </div>
            {form.mode === 'radius' ? (
              <div>
                <label className="text-xs text-gray-500 mb-1 block">Max Distance (miles) *</label>
                <input
                  type="number"
                  placeholder="e.g. 2.5"
                  step="0.1"
                  value={form.maxDistanceMiles}
                  onChange={e => setForm(p => ({ ...p, maxDistanceMiles: e.target.value }))}
                  className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-amber-500 focus:ring-1 focus:ring-amber-500"
                />
              </div>
            ) : (
              <div>
                <label className="text-xs text-gray-500 mb-1 block">Postcode Pattern *</label>
                <input
                  type="text"
                  placeholder="e.g. TS3 8* or ^TS1[A-Z0-9]{3}$"
                  value={form.postcodePattern}
                  onChange={e => setForm(p => ({ ...p, postcodePattern: e.target.value.toUpperCase() }))}
                  className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-amber-500 focus:ring-1 focus:ring-amber-500"
                />
              </div>
            )}
            <div>
              <label className="text-xs text-gray-500 mb-1 block">Delivery Fee (£) *</label>
              <input type="number" placeholder="e.g. 2.50" step="0.01" value={form.fee}
                onChange={e => setForm(p => ({ ...p, fee: e.target.value }))}
                className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-amber-500 focus:ring-1 focus:ring-amber-500" />
            </div>
            <div>
              <label className="text-xs text-gray-500 mb-1 block">Minimum Order (£)</label>
              <input type="number" placeholder="0.00" step="0.01" value={form.minOrder}
                onChange={e => setForm(p => ({ ...p, minOrder: e.target.value }))}
                className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-amber-500 focus:ring-1 focus:ring-amber-500" />
            </div>
            <div className="flex items-center gap-2">
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={form.active}
                  onChange={e => setForm(p => ({ ...p, active: e.target.checked }))}
                  className="w-4 h-4 rounded accent-amber-600" />
                <span className="text-sm text-gray-700">Active</span>
              </label>
            </div>
          </div>
          {error && <p className="text-red-500 text-xs mt-3">{error}</p>}
          <div className="flex gap-3 mt-4">
            <button onClick={handleSave}
              className="bg-gray-900 text-white px-6 py-2.5 rounded-xl text-sm font-semibold hover:bg-amber-700 transition-colors">
              {editing ? 'Update Zone' : 'Add Zone'}
            </button>
            <button onClick={() => { setShowForm(false); setEditing(null); setError(''); }}
              className="px-6 py-2.5 text-gray-600 text-sm font-semibold hover:bg-gray-100 rounded-xl transition-colors">
              Cancel
            </button>
          </div>
        </div>
      )}

      {zones.length === 0 ? (
        <div className="bg-white rounded-2xl border border-gray-200 p-8 text-center">
          <MapPin className="w-12 h-12 text-gray-300 mx-auto mb-3" />
          <h3 className="font-semibold text-gray-900 mb-1">No delivery zones</h3>
          <p className="text-gray-500 text-sm mb-4">Create zones to configure distance-based delivery fees</p>
          <button onClick={openAddForm}
            className="bg-gray-900 text-white px-5 py-2 rounded-xl text-sm font-semibold hover:bg-amber-700 transition-colors">
            Add Your First Zone
          </button>
        </div>
      ) : (
        <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
          <div className="px-4 py-3 bg-amber-50 border-b border-amber-100 flex items-center gap-2">
            <AlertCircle className="w-4 h-4 text-amber-600" />
            <p className="text-xs text-amber-800">Zones are checked in order. Postcode rules are matched first, then distance zones.</p>
          </div>
          <div className="divide-y divide-gray-100">
            {zones.map((zone, idx) => (
              <div key={zone.id} className="flex items-center gap-4 p-4 hover:bg-gray-50 transition-colors">
                <div className="w-8 h-8 bg-amber-100 rounded-lg flex items-center justify-center text-amber-700 font-bold text-sm flex-shrink-0">
                  {idx + 1}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="font-semibold text-gray-900 text-sm">{zone.name}</p>
                    {!zone.active && <span className="text-xs bg-red-100 text-red-600 px-2 py-0.5 rounded-full">Inactive</span>}
                  </div>
                  <p className="text-gray-500 text-xs mt-0.5">
                    {zone.mode === 'postcode'
                      ? `Postcode ${zone.postcodePattern} · £${zone.fee.toFixed(2)} delivery`
                      : `Up to ${zone.maxDistanceMiles} miles · £${zone.fee.toFixed(2)} delivery`
                    }
                    {zone.minOrder > 0 && ` · Min order £${zone.minOrder.toFixed(2)}`}
                  </p>
                </div>
                <div className="flex items-center gap-1">
                  <button onClick={() => openEditForm(zone)}
                    className="p-2 text-gray-400 hover:text-amber-600 rounded-lg hover:bg-amber-50 transition-colors">
                    <Edit className="w-4 h-4" />
                  </button>
                  <button onClick={() => handleDelete(zone.id)}
                    className="p-2 text-gray-400 hover:text-red-500 rounded-lg hover:bg-red-50 transition-colors">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
